import { useState } from 'react';
import type { Movie } from '@/mocks/movies';

interface EpisodePanelProps {
  movie: Movie;
}

export default function EpisodePanel({ movie }: EpisodePanelProps) {
  const isTv = movie.type === 'tv';
  const total = movie.episodes ?? 1;
  const [current, setCurrent] = useState(1);
  const [playing, setPlaying] = useState(false);

  const episodes = Array.from({ length: total }, (_, i) => i + 1);
  const label = isTv ? `第 ${current} 集` : '正片';

  const selectEpisode = (ep: number) => {
    setCurrent(ep);
    setPlaying(false);
  };

  return (
    <div className="px-4 md:px-6 lg:px-10 mt-12">
      <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading flex items-center gap-3 mb-6">
        <span className="w-1.5 h-7 rounded-full bg-primary-500" />
        {isTv ? '剧集播放' : '在线播放'}
      </h2>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
        <div>
          <div className="relative w-full aspect-video rounded-lg overflow-hidden bg-background-950 border border-background-300/60">
            <img
              src={movie.banner ?? movie.cover}
              alt={`${movie.title} ${label} 播放画面`}
              title={`${movie.title} 在线播放`}
              className={`absolute inset-0 w-full h-full object-cover object-top transition-all duration-500 ${
                playing ? 'opacity-70 blur-sm scale-105' : 'opacity-40 blur-[2px]'
              }`}
            />
            <div className="absolute inset-0 bg-background-950/50" />
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
              <button
                type="button"
                onClick={() => setPlaying((v) => !v)}
                aria-label={playing ? '暂停演示' : '播放演示'}
                className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-primary-500 text-background-50 flex items-center justify-center text-3xl md:text-4xl hover:bg-primary-600 transition-colors cursor-pointer"
              >
                <i className={playing ? 'ri-pause-fill' : 'ri-play-fill'} />
              </button>
              <p className="mt-4 text-foreground-900 font-medium">
                {movie.title} · {label}
              </p>
              <p className="mt-1 text-xs text-foreground-500">
                {playing ? '正在演示播放中…（非真实视频）' : '本站为内容演示，不提供真实视频播放'}
              </p>
            </div>
            {playing && (
              <div className="absolute bottom-4 left-4 right-4">
                <div className="h-1 rounded-full bg-background-300/60 overflow-hidden">
                  <div className="h-full w-1/3 rounded-full bg-primary-500 animate-pulse-soft" />
                </div>
              </div>
            )}
          </div>
          <div className="mt-3 flex items-center justify-between text-xs text-foreground-500">
            <span>清晰度：高清 1080P（演示）</span>
            <span>{isTv ? `共 ${total} 集` : movie.duration ?? '电影'}</span>
          </div>
        </div>

        <div className="rounded-lg bg-background-100 border border-background-300/60 p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-foreground-900">
              {isTv ? '选集列表' : '播放项'}
            </h3>
            {isTv && <span className="text-xs text-foreground-500">{movie.updatedTo}</span>}
          </div>
          <div className={isTv ? 'flex gap-2 overflow-x-auto lg:overflow-visible lg:grid lg:grid-cols-4 no-scrollbar pb-1 max-h-[360px] lg:max-h-none lg:overflow-y-auto' : ''}>
            {isTv ? (
              episodes.map((ep) => (
                <button
                  key={ep}
                  type="button"
                  onClick={() => selectEpisode(ep)}
                  className={`shrink-0 w-12 h-10 rounded-md text-sm transition-colors cursor-pointer ${
                    ep === current
                      ? 'bg-primary-500 text-background-50 font-semibold'
                      : 'bg-background-200 text-foreground-700 hover:bg-background-300'
                  }`}
                >
                  {ep}
                </button>
              ))
            ) : (
              <button
                type="button"
                onClick={() => selectEpisode(1)}
                className="w-full h-11 rounded-md bg-primary-500 text-background-50 text-sm font-semibold cursor-pointer flex items-center justify-center gap-2"
              >
                <i className="ri-play-fill text-lg" />
                播放正片
              </button>
            )}
          </div>
          {isTv && (
            <p className="mt-3 text-xs text-foreground-500 leading-relaxed">
              <i className="ri-information-line mr-1" />
              点击集数切换播放演示状态，非真实播放。
            </p>
          )}
        </div>
      </div>
    </div>
  );
}